import { createRouter, createWebHistory } from 'vue-router';

import { useAuthStore } from '@/stores/auth.js';

/**
 * Роуты ЛК.
 *
 * meta.requiresAuth — нужен залогиненный пользователь
 * meta.requiresRole — конкретная роль ЛК в новой модели (см. ADR-018):
 *                     'lk-admin' или '<serviceCode>:<code>'.
 * meta.bare         — рендерить без AppShell (логин, callback)
 */
export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/pages/DashboardPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/pages/ProfilePage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/events',
      name: 'events',
      component: () => import('@/pages/EventsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      // До `/events/:id` — иначе «by» уедет в него как идентификатор.
      path: '/events/by/:source/:externalId',
      name: 'event-resolve',
      component: () => import('@/pages/EventResolvePage.vue'),
      meta: { requiresAuth: true },
    },
    {
      // Раздел участия — частью адреса: страница отвечает на восемь разных
      // вопросов, и на телефоне это шесть экранов прокрутки. Разделённая по
      // адресам, она даёт короткие экраны, рабочую кнопку «назад» и ссылку,
      // которую можно отправить коллеге («вот программа»).
      path: '/events/:id/:section?',
      name: 'event-participation',
      component: () => import('@/pages/EventParticipationPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/services',
      name: 'services',
      component: () => import('@/pages/ServicesPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/footprint',
      name: 'footprint',
      component: () => import('@/pages/DigitalFootprintPage.vue'),
      meta: { requiresAuth: true },
    },
    // --- Админка ---
    {
      path: '/admin/services',
      name: 'admin-services-list',
      component: () => import('@/pages/admin/ServicesListPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/services/new',
      name: 'admin-services-create',
      component: () => import('@/pages/admin/ServiceFormPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/services/:id/edit',
      name: 'admin-services-edit',
      component: () => import('@/pages/admin/ServiceFormPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/users',
      name: 'admin-users-list',
      component: () => import('@/pages/admin/UsersListPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/users/:id',
      name: 'admin-users-detail',
      component: () => import('@/pages/admin/UserDetailPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/groups',
      name: 'admin-groups-list',
      component: () => import('@/pages/admin/GroupsListPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/groups/new',
      name: 'admin-groups-create',
      component: () => import('@/pages/admin/GroupFormPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/groups/:id',
      name: 'admin-groups-detail',
      component: () => import('@/pages/admin/GroupDetailPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/audit',
      name: 'admin-audit',
      component: () => import('@/pages/admin/AuditLogPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/consent',
      name: 'admin-consent',
      component: () => import('@/pages/admin/ConsentPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/content',
      name: 'admin-content-list',
      component: () => import('@/pages/admin/ContentListPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/content/new',
      name: 'admin-content-create',
      component: () => import('@/pages/admin/ContentFormPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    {
      path: '/admin/content/:id/edit',
      name: 'admin-content-edit',
      component: () => import('@/pages/admin/ContentFormPage.vue'),
      meta: { requiresAuth: true, requiresRole: 'lk-admin' },
    },
    // Превью дизайна — только в разработке: экраны кабинета живут за входом,
    // и посмотреть их без учётной записи иначе нельзя. В прод не уезжает.
    ...(import.meta.env.DEV
      ? [
          {
            path: '/preview/design',
            name: 'design-preview',
            component: () => import('@/pages/DesignPreviewPage.vue'),
            meta: { bare: true },
          },
          {
            path: '/preview/participation/:id?/:section?',
            name: 'participation-preview',
            component: () => import('@/pages/preview/ParticipationPreviewPage.vue'),
            meta: { bare: true },
          },
        ]
      : []),

    // --- Auth ---
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/LoginPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/pages/RegisterPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/pages/VerifyEmailPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/pages/ForgotPasswordPage.vue'),
      meta: { bare: true },
    },
    // Установка пароля по ссылке из письма. Вход не требуется по определению:
    // сюда приходят именно потому, что войти не получается.
    {
      path: '/set-password',
      name: 'set-password',
      component: () => import('@/pages/SetPasswordPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/pages/AuthCallbackPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/NotFoundPage.vue'),
    },
  ],
});

router.beforeEach((to) => {
  const authStore = useAuthStore();

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return { name: 'login', query: { returnTo: to.fullPath } };
  }

  if (typeof to.meta.requiresRole === 'string' && !authStore.hasRole(to.meta.requiresRole)) {
    return { name: 'dashboard' };
  }

  // Если пользователь залогинен и идёт на /login или /register — на дашборд
  if ((to.name === 'login' || to.name === 'register') && authStore.isAuthenticated) {
    return { name: 'dashboard' };
  }

  return true;
});
